import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { backendService } from "../services/backend.service";
import type { TeamNurse } from "../utils/models";
import { TEAM_QUERY_STALE_TIME_MS, TEAM_ROSTER_QUERY_KEY } from "./useInvalidateTeamQueries";

/** The Team roster query, shared by the Team page and `useDepartmentStaff` so both hit one cache entry. */
export const teamRosterQueryOptions = (departmentId: string) => ({
	queryKey: [...TEAM_ROSTER_QUERY_KEY, departmentId],
	queryFn: async () => {
		const response = await backendService.getTeamRoster(departmentId);
		return response.data;
	},
	staleTime: TEAM_QUERY_STALE_TIME_MS,
});

/**
 * The Team page roster, fetched on its own instead of read from `hospitalData.nurses`. Every part of the page
 * calls it, and they share one request. Reporting a failure is the page's job (`useTeamFilters`), so one failure
 * is reported once rather than by every caller.
 */
export const useTeamRoster = (departmentId: string) => {
	const query = useQuery<TeamNurse[]>(teamRosterQueryOptions(departmentId));

	// DEFAULT: an empty list while loading or after a failure — the page renders its loading and error states
	// from the flags, so the empty list is never shown as "no staff".
	const roster = useMemo(() => query.data ?? [], [query.data]);

	return { roster, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch };
};
